import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getViewportMode } from '../../../Domain/Scene/Handler/ViewportHandler';
import { meshSelectionStore } from 'Domain/Mesh/State/MeshSelectionStore';

const INITIAL_MOVEMENT_VALUES = {
    distalMesial: "",
    lingualVestibular: "",
    extrusionIntrusion: "",
    torque: "",
    tip: "",
    rotation: "",
};

const INITIAL_STEP_VALUES = {
    distalMesial: 0.1,
    lingualVestibular: 0.1,
    extrusionIntrusion: 0.1,
    torque: 1,
    tip: 1,
    rotation: 1,
};

export const useMovements = () => {
    const dispatch = useDispatch();
    const { ViewportReducer } = useSelector((state) => state);
    const selectedTooth = useSelector((state) => state.ToothReducer.selectedTooth);
    const teethMovements = useSelector((state) => state.ToothReducer.teethMovements);
    const isInterferenceEnabled = useSelector((state) => state.CollisionReducer.isInterferenceEnabled);
    const isStripingEnabled = useSelector((state) => state.CollisionReducer.isStripingEnabled);
    const selectedPointRotation = useSelector((state) => state.ToothReducer.selectedPointRotation);
    const selectedPointTip = useSelector((state) => state.ToothReducer.selectedPointTip);
    const selectedPointTorque = useSelector((state) => state.ToothReducer.selectedPointTorque);

    const [movementValues, setMovementValues] = useState(INITIAL_MOVEMENT_VALUES);
    const [isStepEditMode, setIsStepEditMode] = useState(false);
    const [stepValues, setStepValues] = useState(INITIAL_STEP_VALUES);
    const initialViewportMode = getViewportMode();
    const isInitiallySingle = initialViewportMode === "single";
    const isInitiallyShort = window.innerHeight < 800;
    const canUseLeftPanel = isInitiallySingle && !isInitiallyShort;
    const [viewModeSingle, setViewModeSingle] = useState(canUseLeftPanel);
    const [panelStyle, setPanelStyle] = useState(canUseLeftPanel ? "LeftPanel" : "BottomPanel");

    const currentTooth = meshSelectionStore.getClickedTooth();

    const updateViewMode = useCallback(() => {
        const isSingleMode = ViewportReducer.viewportMode === "single";
        const isShortScreen = window.innerHeight < 800;
        const canVertical = isSingleMode && !isShortScreen;
        setViewModeSingle(canVertical);
        setPanelStyle(canVertical ? "LeftPanel" : "BottomPanel");
    }, [ViewportReducer.viewportMode]);

    useEffect(() => {
        window.addEventListener("resize", updateViewMode);
        updateViewMode();
        return () => {
            window.removeEventListener("resize", updateViewMode);
        };
    }, [updateViewMode]);

    useEffect(() => {
        const clicked = meshSelectionStore.getClickedTooth();
        const hasClickedTooth = !!clicked;
        if (!hasClickedTooth) {
            setMovementValues(INITIAL_MOVEMENT_VALUES);
            return;
        }

        if (!selectedTooth) return;
        const id = selectedTooth.id;
        const toothMovement = teethMovements[id] || {};
        const formatValue = (val) => val?.toFixed(2) || "";

        setMovementValues({
            distalMesial: formatValue(toothMovement.distalMesial),
            lingualVestibular: formatValue(toothMovement.lingualVestibular),
            extrusionIntrusion: formatValue(toothMovement.extrusionIntrusion),
            torque: formatValue(toothMovement.torque),
            tip: formatValue(toothMovement.tip),
            rotation: formatValue(toothMovement.rotation),
        });
    }, [selectedTooth, teethMovements]);

    const handleValueChange = (movementType, newValue) => {
        setMovementValues((prev) => ({
            ...prev,
            [movementType]: newValue,
        }));
    };

    return {
        dispatch,
        selectedTooth,
        teethMovements,
        isInterferenceEnabled,
        isStripingEnabled,
        selectedPointRotation,
        selectedPointTip,
        selectedPointTorque,
        movementValues,
        setMovementValues,
        isStepEditMode,
        setIsStepEditMode,
        stepValues,
        setStepValues,
        viewModeSingle,
        panelStyle,
        currentTooth,
        handleValueChange,
    };
};
